import { SelectOption } from "components/dropdown/Dropdown";
import { FormikContextType, useField, useFormikContext } from "formik";
import { useCallback, useEffect, useState } from "react";

import { MultiSelectOption } from "../../../dropdown/MultiDropdown";

import Select from "react-select";
import { ValueType } from "react-select/src/types";
import {
  CustomControl,
  CustomOption,
  CustomValue,
  customUseOnClickOutside,
} from "./SearchListRenderer";
import api from "api";
import { debounce } from "lodash";
import { useOnceAfterCondition } from "helpers/useOnceAfterCondition";
import { Option } from "react-select/src/filters";
import { SingleSelectOption } from "../SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { toast } from "react-toastify";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";

const capitaliseFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export function SingleSelectSearchList(props: {
  isMulti?: boolean;
  name: string;
  dataSource: string | null;
  showUseDefaultValues?: boolean;
  useDefaultValue?: boolean;
  limitOptionsToDefaultValue?: boolean;
  searchEndpoint?: string | { [key: string]: string };
  readOnly?: boolean;
  addZIndexToMenu?: boolean;
  overrideOptions?: {
    [fieldName: string]: (
      formik: FormikContextType<any>
    ) => [SingleSelectOption[], string?, string?];
  };
  callSearchEndpointOnClick?: boolean;
}) {
  const [field] = useField<SelectOption>(props.name);
  const [options, setOptions] = useState<SelectOption[] | null>(null);
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null
  );

  const [selectedOptions, setSelectedOptions] = useState<SelectOption[] | null>(
    null
  );
  const [initialOption, setInitialOption] = useState<{
    label: string;
    value: number | null;
  }>({
    label: "",
    value: null,
  });
  const [defaultValue, setDefaultValue] = useState<any>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [parsedSearchEndpoint, setParsedSearchEndpoint] = useState<
    string | null
  >(null);
  const [searchString, setSearchString] = useState("");

  useEffect(() => {
    if (props.searchEndpoint) {
      if (
        typeof props.searchEndpoint === "object" &&
        props.searchEndpoint.hasOwnProperty(props.name)
      ) {
        setParsedSearchEndpoint(
          props.searchEndpoint[props.name as keyof typeof props.searchEndpoint]
        );
      } else if (typeof props.searchEndpoint === "string") {
        setParsedSearchEndpoint(props.searchEndpoint);
      }
    }
  }, [props.searchEndpoint]);

  const bind = customUseOnClickOutside(() => setMenuOpen(false), true);

  const formik = useFormikContext<SelectOption>();
  const {
    values: formValues,
    setTouched,
    touched: formikTouched,
    setFieldValue,
    setValues,
  } = useFormikContext<{
    [key: string]: any;
  }>();
  const formikErrors = formik.errors;

  let relevantFormikError = null;

  if (formikErrors.hasOwnProperty(field.name) && formik.submitCount > 0) {
    const error = formikErrors[field.name as keyof typeof formikErrors];

    if (typeof error === "string") {
      relevantFormikError =
        formikErrors[field.name as keyof typeof formikErrors];
    }
  }

  useEffect(() => {
    let tempInitialOption = {
      label: "",
      value: null,
    };

    if (field && typeof field === "object" && field.hasOwnProperty("value")) {
      if (typeof field.value === "number") {
        tempInitialOption["value"] = field.value;
      }
    }

    const dataSource = props.dataSource;

    if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource) &&
      Array.isArray(formik.values[dataSource as keyof typeof formik.values])
    ) {
      const data = formik.values[dataSource as keyof typeof formik.values];

      if (data && Array.isArray(data)) {
        const tempOptions = data.map((item: SelectOption, idx: number) => {
          if (
            typeof tempInitialOption.value === "number" &&
            item.value === tempInitialOption.value
          ) {
            tempInitialOption["label"] = item.label;
          }

          return {
            label: item.label,

            value: item.value,
          };
        });

        setOptions(tempOptions);
        setInitialOption(tempInitialOption);

        if (props.useDefaultValue) {
          const defaultValueFieldName = `default${capitaliseFirstLetter(
            field.name
          )}`;

          if (formik.values.hasOwnProperty(defaultValueFieldName)) {
            const defaultValueData: any =
              formik.values[
                defaultValueFieldName as keyof typeof formik.values
              ];

            const dataSourceMatch = data.filter(
              item => item.value && item.value === defaultValueData
            );

            if (
              dataSourceMatch.length === 1 &&
              typeof dataSourceMatch[0] === "object" &&
              dataSourceMatch[0].hasOwnProperty("label") &&
              dataSourceMatch[0].hasOwnProperty("value")
            ) {
              const tempDefaultValue = {
                label: dataSourceMatch[0].label,
                value: dataSourceMatch[0].value,
              };

              setDefaultValue(tempDefaultValue);

              // Only use default value if we haven't already set an initial option from previously selected options above
              if (!tempInitialOption.value) {
                setInitialOption(tempDefaultValue);
              }
            }
          }
        }
      }
    } else if (
      formik &&
      formik.values &&
      typeof formik.values === "object" &&
      dataSource &&
      formik.values.hasOwnProperty(dataSource)
    ) {
      setOptions([]);
    }

    if (
      tempInitialOption.label !== "" &&
      typeof tempInitialOption.value === "number" &&
      selectedOption === null
    ) {
      setSelectedOption(tempInitialOption);
    }
  }, []);

  useEffect(() => {
    if (
      initialOption &&
      initialOption.label !== "" &&
      initialOption.value !== null &&
      !selectedOption
    ) {
      setSelectedOption(initialOption);
      formik.setFieldValue(field.name, initialOption.value);
    }
  }, [initialOption]);

  useEffect(() => {
    if (defaultValue) {
      if (props.limitOptionsToDefaultValue) {
        setOptions([defaultValue]);

        if (
          initialOption.label !== defaultValue.label &&
          initialOption.value !== defaultValue.value
        ) {
          setOptions([defaultValue, initialOption]);
        } else {
          setOptions([defaultValue]);
        }
      }

      if (!selectedOption) {
        setSelectedOption(defaultValue);
        formik.setFieldValue(field.name, defaultValue.value);
      }
    }
  }, [defaultValue]);

  // Make sure field value is in the list of options, if not, set to default value or empty value
  useEffect(() => {
    if (
      options &&
      Array.isArray(options) &&
      options.length > 0 &&
      options[0].hasOwnProperty("value") &&
      field.value
    ) {
      if (props.isMulti) {
        const valueMatch: SelectOption[] | null = [];

        if (Array.isArray(field.value) && field.value.length >= 1) {
          field.value.forEach(id => {
            let match = options.filter(item => item.value === id);
            if (match.length === 1) {
              let option = match[0];
              valueMatch.push(option);
            }
          });
        }
        if (valueMatch.length >= 1) {
          setSelectedOptions(valueMatch);
        }
      }

      const valueMatch = options.filter(item => item.value === field.value);

      if (valueMatch.length === 0) {
        if (defaultValue) {
          setSelectedOption(defaultValue);
          if (defaultValue.hasOwnProperty("value")) {
            formik.setFieldValue(field.name, defaultValue.value);
          }
        } else {
          setSelectedOption({
            label: "",
            value: null,
          });
          formik.setFieldValue(field.name, null);
        }
      } else if (valueMatch.length === 1) {
        setSelectedOption(valueMatch[0]);
      }
    }
  }, [field]);

  const onChange = (v: MultiSelectOption | MultiSelectOption[]) => {
    if (props.isMulti && Array.isArray(v)) {
      setSelectedOptions(v);
      // console.log(field.value);
      let finalValues = v.map(val => val.value);
      setFieldValue(field.name, finalValues);
    } else if (!Array.isArray(v)) {
      setSelectedOption(v);
      if (typeof v.value === "string") {
        formik.setFieldValue(field.name, parseInt(v.value));
      } else {
        formik.setFieldValue(field.name, v.value);
      }
    }
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [selectedOption]);

  const fetchResults = async (
    searchString: string,
    parsedSearchEndpoint: string
  ) => {
    if (parsedSearchEndpoint) {
      try {
        const results = await api.get(parsedSearchEndpoint, {
          params: { searchString },
        });

        let options: SelectOption[] | null = null;

        if (
          results &&
          results.data &&
          results.data.status === "1" &&
          results.data.details.results &&
          Array.isArray(results.data.details.results)
        ) {
          options = results.data.details.results;
        }

        setOptions(options);
      } catch (error) {
        // Error case
        console.log("fetchResults error", error);
      }
    }
  };

  const getSearchResults = useCallback(debounce(fetchResults, 500), []);

  const onFilter = (e: any) => {
    e.preventDefault();
    let inputString = e.target.value;
    if (e.target.value === "") {
      setSearchString("");
      setDefaultOpts();
    } else {
      setSearchString(inputString);
      setOptions(
        formik.values[
          props.dataSource as keyof typeof formik.values
        ].filter((opt: SelectOption) =>
          opt.label.toLowerCase().includes(inputString.toLowerCase())
        ) ?? null
      );
    }
  };

  const onSearch = (e: any) => {
    e.preventDefault();
    if (!e.target.value) {
      setSearchString("");
      setDefaultOpts();
    } else if (parsedSearchEndpoint && e.target.value) {
      setSearchString(e.target.value);
      getSearchResults(e.target.value, parsedSearchEndpoint);
    }
  };

  const Remove = () => {
    setSelectedOption(null);
    setFieldValue(props.name, null);
  };

  const setDefaultOpts = () => {
    if (
      props.dataSource &&
      formik.values &&
      formik.values.hasOwnProperty(props.dataSource)
    ) {
      const data = formik.values[
        props.dataSource as keyof typeof formik.values
      ] as SelectOption[];
      setOptions(data ?? []);
    }
  };

  useEffect(() => {
    if (
      props.overrideOptions &&
      props.overrideOptions.hasOwnProperty(props.name)
    ) {
      const overrideFunction = props.overrideOptions[props.name];
      const [tempOptions, resetField, triggerField] = overrideFunction(formik);
      if (
        field.value !== null &&
        !tempOptions.some(item =>
          typeof field.value === "number"
            ? item.value === field.value
            : item.value === field.value.value
        )
      ) {
        setFieldValue(field.name, null);
        setSelectedOption(null);
        toast(
          <ToastMessage
            toastData={[
              {
                title: `${resetField} Reset`,
                text: `${resetField} no longer available for chosen ${triggerField}`,
              },
            ]}
            className="ToastWarning"
          />,
          {
            ...toastConfig,
          }
        );
      }
      setOptions(tempOptions);
    }
  }, [props.overrideOptions, formik.values]);

  useOnceAfterCondition(
    () => {
      if (!selectedOption) return;

      if (!Array.isArray(selectedOption)) {
        const startOps = options?.filter(
          opt => selectedOption.value === opt.value
        );

        setSelectedOption(
          startOps &&
            startOps.length === 1 &&
            typeof startOps[0] === "object" &&
            startOps[0].hasOwnProperty("value")
            ? startOps[0]
            : null
        );
      }
    },
    [options],
    options
  );

  const filterOption = (option: Option, rawInput: string) => {
    // Return true for all options as we are already filtering when calling the search endpoint. We don't need to filter twice
    return true;
  };

  if (options) {
    return (
      <div className="FormField">
        <div
          className={`EditSingleSelectFieldContainer SearchListRenderer ${
            relevantFormikError ? "Error" : ""
          } ${props.readOnly ? "EditReadOnly" : ""} ${field.name}           ${
            props.addZIndexToMenu ? "AddZIndexToMenu" : ""
          }`}
          {...bind}
        >
          <Select
            className={`CustomSelect EditSingleSelect SearchListRenderer ${
              !props.isMulti ? "Single" : ""
            }`}
            classNamePrefix={`CustomSelect${
              relevantFormikError ? "Error" : ""
            }`}
            options={options ?? []}
            value={
              props.isMulti
                ? selectedOptions
                : selectedOption
                ? selectedOption
                : field.value
            }
            onChange={
              onChange as (selectedOption: ValueType<SelectOption, any>) => void
            }
            selectProps={{
              menuOpen: menuOpen,
              setMenuOpen: setMenuOpen,
              remove: Remove,
              searchString,
              parsedSearchEndpoint,
              fetchResults,
              callSearchEndpointOnClick: props.callSearchEndpointOnClick,
            }}
            isMulti={props.isMulti ?? false}
            filterOption={parsedSearchEndpoint ? filterOption : undefined}
            isSearchable={false}
            menuIsOpen={menuOpen}
            onMenuOpen={() => {
              setMenuOpen(true); // NOTE: This is needed to make click on mobile phones work
            }}
            onMenuClose={() => {
              // setMenuOpen(false); // NOTE: This is needed to make click on mobile phones work
            }}
            noOptionsMessage={() => "No options"}
            placeholder={false}
            closeMenuOnSelect={false}
            components={{
              Option: CustomOption,
              Control: CustomControl,
              SingleValue: CustomValue,
            }}
            onBlur={() => {
              let newTouched = { ...formikTouched };
              newTouched[props.name] = true;
              setTouched(newTouched);
            }}
            styles={{
              multiValue: (styles, { data }) => {
                return {
                  ...styles,
                  backgroundColor: "#DEEBFF",
                  color: "#4C9AFF",
                };
              },
              multiValueLabel: (styles, { data }) => ({
                ...styles,
                color: "#4C9AFF",
                fontSize: "1.1rem",
              }),
              multiValueRemove: (styles, { data }) => ({
                ...styles,
                color: "#4C9AFF",
                ":hover": {
                  backgroundColor: "red",
                  color: "white",
                },
              }),
            }}
            backspaceRemovesValue={false} // Prevent backspace key presses deleting selected items
          />
          <div className={`${menuOpen ? "behind" : "hide"}`}>
            <input
              className="custom-input-search"
              value={parsedSearchEndpoint ? searchString : undefined}
              onChange={parsedSearchEndpoint ? onSearch : onFilter}
              placeholder="Type search here"
              onFocus={() => {
                let newTouched = { ...formikTouched };
                newTouched[props.name] = false;
                setTouched(newTouched);
              }}
              onBlur={() => {
                let newTouched = { ...formikTouched };
                newTouched[props.name] = true;
                setTimeout(() => {
                  setTouched(newTouched);
                }, 100);
              }}
            />
          </div>
          {props.showUseDefaultValues && (
            <div
              className={`UseDefaultValuesBtn Btn ${
                !selectedOption ? "Disabled" : ""
              }`}
              onClick={() => {
                if (selectedOption && selectedOption.value) {
                }
              }}
            >
              <div className="Text">Use Default Values</div>
            </div>
          )}
          {relevantFormikError && (
            <div className="ErrorLabel">{relevantFormikError}</div>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <div
        className={`EditSingleSelectFieldContainer ${
          relevantFormikError ? "Error" : ""
        }`}
      >
        <div className="EditBox ViewOnly NoOptions">Error: No data</div>

        {relevantFormikError && (
          <div className="ErrorLabel">{relevantFormikError}</div>
        )}
      </div>
    );
  }
}
